import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Rating,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Facebook,
  Instagram,
  Twitter,
  LinkedIn,
  YouTube,
  Add,
  Edit,
  Delete,
  Schedule,
  Send,
  Reply,
  TrendingUp,
  Visibility,
  ThumbUp,
  Comment,
  Share,
  Star,
  Warning,
  CheckCircle,
  Campaign,
  Analytics,
  Settings,
  Refresh
} from '@mui/icons-material';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`social-media-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface SocialMediaAccount {
  _id: string;
  platform: string;
  accountName: string;
  isActive: boolean;
  lastSync: string;
  settings: {
    autoPost: boolean;
    autoReply: boolean;
    postFrequency: string;
    mentionMonitoring: boolean;
  };
}

interface SocialMediaPost {
  _id: string;
  platform: string;
  content: {
    text: string;
    images: string[];
    hashtags: string[];
  };
  status: string;
  publishedAt?: string;
  scheduledFor?: string;
  engagement: {
    likes: number;
    comments: number;
    shares: number;
    views: number;
  };
  type: string;
}

interface Review {
  _id: string;
  platform: string;
  reviewer: {
    name: string;
    avatar?: string;
  };
  rating: number;
  title?: string;
  content: string;
  reviewDate: string;
  sentiment: string;
  needsAttention: boolean;
  response?: {
    content: string;
    respondedAt: string;
  };
}

interface Campaign {
  _id: string;
  name: string;
  type: string;
  status: string;
  platforms: string[];
  metrics: {
    postsCreated: number;
    impressions: number;
    engagement: number;
  };
  createdAt: string;
}

interface Analytics {
  metrics: {
    followers: {
      start: number;
      end: number;
      growth: number;
      growthRate: number;
    };
    engagement: {
      totalLikes: number;
      totalComments: number;
      totalShares: number;
      engagementRate: number;
    };
    posts: {
      published: number;
      scheduled: number;
      topPerforming: Array<{
        postId: string;
        engagement: number;
        reach: number;
      }>;
    };
    reviews: {
      total: number;
      averageRating: number;
      responseRate: number;
      sentimentBreakdown: {
        positive: number;
        neutral: number;
        negative: number;
      };
    };
  };
  insights: {
    bestPostingTimes: string[];
    topHashtags: string[];
    recommendations: string[];
  };
}

const SocialMediaPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [accounts, setAccounts] = useState<SocialMediaAccount[]>([]);
  const [posts, setPosts] = useState<SocialMediaPost[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState('PRWR');

  // Dialog states
  const [connectAccountDialog, setConnectAccountDialog] = useState(false);
  const [createPostDialog, setCreatePostDialog] = useState(false);
  const [createCampaignDialog, setCreateCampaignDialog] = useState(false);
  const [reviewResponseDialog, setReviewResponseDialog] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);

  // Form states
  const [newAccountData, setNewAccountData] = useState({
    platform: '',
    accessToken: '',
    accountName: ''
  });
  const [newPostData, setNewPostData] = useState({
    platform: '',
    content: '',
    hashtags: '',
    scheduledFor: '',
    type: 'promotional'
  });
  const [newCampaignData, setNewCampaignData] = useState({
    name: '',
    type: 'promotional',
    platforms: [] as string[],
    description: ''
  });
  const [reviewResponse, setReviewResponse] = useState('');

  const platformIcons: { [key: string]: React.ReactElement } = {
    facebook: <Facebook />,
    instagram: <Instagram />,
    twitter: <Twitter />,
    linkedin: <LinkedIn />,
    youtube: <YouTube />
  };

  const platformColors: { [key: string]: string } = {
    facebook: '#1877F2',
    instagram: '#E4405F',
    twitter: '#1DA1F2',
    linkedin: '#0A66C2',
    youtube: '#FF0000'
  };

  useEffect(() => {
    fetchData();
  }, [selectedLocation]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAccounts(),
        fetchPosts(),
        fetchReviews(),
        fetchCampaigns(),
        fetchAnalytics()
      ]);
    } catch (error) {
      console.error('Error fetching social media data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await fetch(`/api/social-media/accounts/${selectedLocation}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setAccounts(data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await fetch(`/api/social-media/posts/${selectedLocation}?limit=20`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setPosts(data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await fetch(`/api/social-media/reviews/${selectedLocation}?limit=20`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setReviews(data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await fetch(`/api/social-media/campaigns/${selectedLocation}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setCampaigns(data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`/api/social-media/analytics/${selectedLocation}?period=monthly`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleConnectAccount = async () => {
    try {
      const response = await fetch('/api/social-media/accounts/connect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          locationId: selectedLocation,
          platform: newAccountData.platform,
          authData: {
            accessToken: newAccountData.accessToken,
            accountName: newAccountData.accountName
          }
        })
      });

      if (response.ok) {
        setConnectAccountDialog(false);
        setNewAccountData({ platform: '', accessToken: '', accountName: '' });
        fetchAccounts();
      }
    } catch (error) {
      console.error('Error connecting account:', error);
    }
  };

  const handleCreatePost = async () => {
    try {
      const selectedAccount = accounts.find(acc => acc.platform === newPostData.platform);
      if (!selectedAccount) return;

      const response = await fetch('/api/social-media/posts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          locationId: selectedLocation,
          accountId: selectedAccount._id,
          platform: newPostData.platform,
          content: {
            text: newPostData.content,
            hashtags: newPostData.hashtags.split(',').map(h => h.trim()).filter(h => h)
          },
          type: newPostData.type,
          scheduledFor: newPostData.scheduledFor || undefined,
          publishNow: !newPostData.scheduledFor
        })
      });

      if (response.ok) {
        setCreatePostDialog(false);
        setNewPostData({ platform: '', content: '', hashtags: '', scheduledFor: '', type: 'promotional' });
        fetchPosts();
      }
    } catch (error) {
      console.error('Error creating post:', error);
    }
  };

  const handleCreateCampaign = async () => {
    try {
      const response = await fetch('/api/social-media/campaigns', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          locationId: selectedLocation,
          ...newCampaignData,
          content: {
            schedule: {
              startDate: new Date(),
              frequency: 'weekly'
            }
          }
        })
      });

      if (response.ok) {
        setCreateCampaignDialog(false);
        setNewCampaignData({ name: '', type: 'promotional', platforms: [], description: '' });
        fetchCampaigns();
      }
    } catch (error) {
      console.error('Error creating campaign:', error);
    }
  };

  const handleRespondToReview = async () => {
    if (!selectedReview || !reviewResponse.trim()) return;

    try {
      const response = await fetch(`/api/social-media/reviews/${selectedReview._id}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ responseContent: reviewResponse })
      });

      if (response.ok) {
        setReviewResponseDialog(false);
        setSelectedReview(null);
        setReviewResponse('');
        fetchReviews();
      }
    } catch (error) {
      console.error('Error responding to review:', error);
    }
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return '#4caf50';
      case 'negative': return '#f44336';
      case 'neutral': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return '#4caf50';
      case 'scheduled': return '#2196f3';
      case 'draft': return '#ff9800';
      case 'failed': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Social Media Management
        </Typography>
        <Box>
          <FormControl size="small" sx={{ minWidth: 120, mr: 2 }}>
            <InputLabel>Location</InputLabel>
            <Select
              value={selectedLocation}
              label="Location"
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              <MenuItem value="PRWR">Pioneer RV</MenuItem>
              <MenuItem value="ARVA">Arrowhead RV</MenuItem>
              <MenuItem value="SRMA">Sunridge RV</MenuItem>
              <MenuItem value="SPRI">Sunset Palm RV</MenuItem>
              <MenuItem value="AVSW">Adobe Village RV</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Overview" />
          <Tab label="Accounts" />
          <Tab label="Posts" />
          <Tab label="Reviews" />
          <Tab label="Campaigns" />
          <Tab label="Analytics" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            {/* Key Metrics Cards */}
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: '#1877F2', mr: 2 }}>
                      <TrendingUp />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{analytics?.metrics.followers.end || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Followers
                      </Typography>
                      <Typography variant="caption" color="success.main">
                        +{analytics?.metrics.followers.growth || 0} this month
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: '#E4405F', mr: 2 }}>
                      <ThumbUp />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {((analytics?.metrics.engagement.totalLikes || 0) + 
                          (analytics?.metrics.engagement.totalComments || 0) + 
                          (analytics?.metrics.engagement.totalShares || 0)).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Engagement
                      </Typography>
                      <Typography variant="caption" color="primary.main">
                        {analytics?.metrics.engagement.engagementRate?.toFixed(1) || 0}% rate
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: '#4caf50', mr: 2 }}>
                      <Star />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {analytics?.metrics.reviews.averageRating?.toFixed(1) || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Average Rating
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {analytics?.metrics.reviews.total || 0} reviews
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ bgcolor: '#ff9800', mr: 2 }}>
                      <Campaign />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{campaigns.filter(c => c.status === 'active').length}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active Campaigns
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {campaigns.length} total
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Activity */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Recent Posts Performance</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={posts.slice(0, 5).map(post => ({
                      name: post.content.text.substring(0, 20) + '...',
                      likes: post.engagement.likes,
                      comments: post.engagement.comments,
                      shares: post.engagement.shares
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="likes" fill="#2196f3" />
                      <Bar dataKey="comments" fill="#4caf50" />
                      <Bar dataKey="shares" fill="#ff9800" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Platform Distribution */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Platform Distribution</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={accounts.map(account => ({
                          name: account.platform,
                          value: posts.filter(p => p.platform === account.platform).length,
                          fill: platformColors[account.platform] || '#9e9e9e'
                        }))}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label
                      />
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Quick Actions */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Quick Actions</Typography>
                  <Box display="flex" gap={2} flexWrap="wrap">
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => setConnectAccountDialog(true)}
                    >
                      Connect Account
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Edit />}
                      onClick={() => setCreatePostDialog(true)}
                    >
                      Create Post
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Campaign />}
                      onClick={() => setCreateCampaignDialog(true)}
                    >
                      New Campaign
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Reply />}
                      onClick={() => setActiveTab(3)}
                    >
                      Review Responses ({reviews.filter(r => r.needsAttention).length})
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Connected Accounts</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setConnectAccountDialog(true)}
            >
              Connect Account
            </Button>
          </Box>

          <Grid container spacing={3}>
            {accounts.map((account) => (
              <Grid item xs={12} md={6} lg={4} key={account._id}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Avatar sx={{ bgcolor: platformColors[account.platform], mr: 2 }}>
                        {platformIcons[account.platform]}
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="h6">{account.accountName}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {account.platform.charAt(0).toUpperCase() + account.platform.slice(1)}
                        </Typography>
                      </Box>
                      <Chip
                        label={account.isActive ? 'Active' : 'Inactive'}
                        color={account.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>

                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        Last sync: {new Date(account.lastSync).toLocaleDateString()}
                      </Typography>
                    </Box>

                    <Box>
                      <FormControlLabel
                        control={<Switch checked={account.settings.autoPost} size="small" />}
                        label="Auto Post"
                        sx={{ mb: 1 }}
                      />
                      <FormControlLabel
                        control={<Switch checked={account.settings.autoReply} size="small" />}
                        label="Auto Reply"
                        sx={{ mb: 1 }}
                      />
                      <FormControlLabel
                        control={<Switch checked={account.settings.mentionMonitoring} size="small" />}
                        label="Monitor Mentions"
                      />
                    </Box>

                    <Box mt={2} display="flex" gap={1}>
                      <Button size="small" startIcon={<Settings />}>
                        Settings
                      </Button>
                      <Button size="small" startIcon={<Analytics />}>
                        Analytics
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Posts</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreatePostDialog(true)}
            >
              Create Post
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Platform</TableCell>
                  <TableCell>Content</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Engagement</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {posts.map((post) => (
                  <TableRow key={post._id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ bgcolor: platformColors[post.platform], mr: 1, width: 24, height: 24 }}>
                          {platformIcons[post.platform]}
                        </Avatar>
                        {post.platform.charAt(0).toUpperCase() + post.platform.slice(1)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {post.content.text.substring(0, 100)}...
                      </Typography>
                      {post.content.hashtags.length > 0 && (
                        <Box mt={1}>
                          {post.content.hashtags.slice(0, 3).map((tag, index) => (
                            <Chip key={index} label={tag} size="small" sx={{ mr: 0.5 }} />
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={post.status}
                        size="small"
                        sx={{ bgcolor: getStatusColor(post.status), color: 'white' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box display="flex" alignItems="center">
                          <ThumbUp fontSize="small" color="action" />
                          <Typography variant="caption" ml={0.5}>
                            {post.engagement.likes}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Comment fontSize="small" color="action" />
                          <Typography variant="caption" ml={0.5}>
                            {post.engagement.comments}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                          <Share fontSize="small" color="action" />
                          <Typography variant="caption" ml={0.5}>
                            {post.engagement.shares}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {post.publishedAt 
                          ? new Date(post.publishedAt).toLocaleDateString()
                          : post.scheduledFor 
                            ? `Scheduled: ${new Date(post.scheduledFor).toLocaleDateString()}`
                            : 'Draft'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <Edit />
                      </IconButton>
                      <IconButton size="small">
                        <Delete />
                      </IconButton>
                      {post.status === 'draft' && (
                        <IconButton size="small">
                          <Send />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Reviews & Feedback</Typography>
            <Box>
              <Button variant="outlined" startIcon={<Refresh />}>
                Sync Reviews
              </Button>
            </Box>
          </Box>

          {/* Review Summary */}
          <Grid container spacing={3} mb={3}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4">{analytics?.metrics.reviews.averageRating?.toFixed(1) || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Average Rating</Typography>
                  <Rating value={analytics?.metrics.reviews.averageRating || 0} readOnly />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4">{analytics?.metrics.reviews.total || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Reviews</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4">{analytics?.metrics.reviews.responseRate || 0}%</Typography>
                  <Typography variant="body2" color="text.secondary">Response Rate</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h4">{reviews.filter(r => r.needsAttention).length}</Typography>
                  <Typography variant="body2" color="text.secondary">Needs Attention</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <List>
            {reviews.map((review) => (
              <React.Fragment key={review._id}>
                <ListItem alignItems="flex-start">
                  <ListItemAvatar>
                    <Avatar src={review.reviewer.avatar}>
                      {review.reviewer.name.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1">{review.reviewer.name}</Typography>
                        <Rating value={review.rating} readOnly size="small" />
                        <Chip
                          label={review.platform}
                          size="small"
                          sx={{ bgcolor: platformColors[review.platform], color: 'white' }}
                        />
                        <Chip
                          label={review.sentiment}
                          size="small"
                          sx={{ bgcolor: getSentimentColor(review.sentiment), color: 'white' }}
                        />
                        {review.needsAttention && (
                          <Warning color="warning" fontSize="small" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        {review.title && (
                          <Typography variant="subtitle2" gutterBottom>
                            {review.title}
                          </Typography>
                        )}
                        <Typography variant="body2" paragraph>
                          {review.content}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(review.reviewDate).toLocaleDateString()}
                        </Typography>
                        {review.response ? (
                          <Box mt={1} p={2} bgcolor="grey.100" borderRadius={1}>
                            <Typography variant="body2">
                              <strong>Response:</strong> {review.response.content}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Responded on {new Date(review.response.respondedAt).toLocaleDateString()}
                            </Typography>
                          </Box>
                        ) : (
                          <Box mt={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Reply />}
                              onClick={() => {
                                setSelectedReview(review);
                                setReviewResponseDialog(true);
                              }}
                            >
                              Respond
                            </Button>
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                <Divider variant="inset" component="li" />
              </React.Fragment>
            ))}
          </List>
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">Marketing Campaigns</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateCampaignDialog(true)}
            >
              Create Campaign
            </Button>
          </Box>

          <Grid container spacing={3}>
            {campaigns.map((campaign) => (
              <Grid item xs={12} md={6} lg={4} key={campaign._id}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Typography variant="h6">{campaign.name}</Typography>
                      <Chip
                        label={campaign.status}
                        color={campaign.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {campaign.type.charAt(0).toUpperCase() + campaign.type.slice(1)}
                    </Typography>

                    <Box display="flex" gap={1} mb={2}>
                      {campaign.platforms.map((platform) => (
                        <Avatar
                          key={platform}
                          sx={{ bgcolor: platformColors[platform], width: 24, height: 24 }}
                        >
                          {platformIcons[platform]}
                        </Avatar>
                      ))}
                    </Box>

                    <Box mb={2}>
                      <Typography variant="body2">
                        Posts Created: {campaign.metrics.postsCreated}
                      </Typography>
                      <Typography variant="body2">
                        Impressions: {campaign.metrics.impressions.toLocaleString()}
                      </Typography>
                      <Typography variant="body2">
                        Engagement: {campaign.metrics.engagement.toLocaleString()}
                      </Typography>
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      Created: {new Date(campaign.createdAt).toLocaleDateString()}
                    </Typography>

                    <Box mt={2} display="flex" gap={1}>
                      <Button size="small" variant="outlined">
                        Edit
                      </Button>
                      <Button size="small" variant="outlined">
                        Analytics
                      </Button>
                      {campaign.status === 'draft' && (
                        <Button size="small" variant="contained">
                          Launch
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          <Typography variant="h6" gutterBottom>Analytics & Insights</Typography>

          <Grid container spacing={3}>
            {/* Follower Growth Chart */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Follower Growth</Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={[
                      { name: 'Jan', followers: 920 },
                      { name: 'Feb', followers: 950 },
                      { name: 'Mar', followers: 980 },
                      { name: 'Apr', followers: 1020 },
                      { name: 'May', followers: 1050 }
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="followers" stroke="#2196f3" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Best Posting Times */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Best Posting Times</Typography>
                  <List dense>
                    {analytics?.insights.bestPostingTimes.map((time, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={time} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Top Hashtags */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Top Hashtags</Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {analytics?.insights.topHashtags.map((hashtag, index) => (
                      <Chip key={index} label={hashtag} variant="outlined" />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Recommendations */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>AI Recommendations</Typography>
                  <List dense>
                    {analytics?.insights.recommendations.map((rec, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={rec} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Connect Account Dialog */}
      <Dialog open={connectAccountDialog} onClose={() => setConnectAccountDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Connect Social Media Account</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Platform</InputLabel>
            <Select
              value={newAccountData.platform}
              label="Platform"
              onChange={(e) => setNewAccountData({ ...newAccountData, platform: e.target.value })}
            >
              <MenuItem value="facebook">Facebook</MenuItem>
              <MenuItem value="instagram">Instagram</MenuItem>
              <MenuItem value="twitter">Twitter</MenuItem>
              <MenuItem value="linkedin">LinkedIn</MenuItem>
              <MenuItem value="youtube">YouTube</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Account Name"
            value={newAccountData.accountName}
            onChange={(e) => setNewAccountData({ ...newAccountData, accountName: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Access Token"
            type="password"
            value={newAccountData.accessToken}
            onChange={(e) => setNewAccountData({ ...newAccountData, accessToken: e.target.value })}
            helperText="Get this from your platform's developer console"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectAccountDialog(false)}>Cancel</Button>
          <Button onClick={handleConnectAccount} variant="contained">Connect</Button>
        </DialogActions>
      </Dialog>

      {/* Create Post Dialog */}
      <Dialog open={createPostDialog} onClose={() => setCreatePostDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Post</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Platform</InputLabel>
            <Select
              value={newPostData.platform}
              label="Platform"
              onChange={(e) => setNewPostData({ ...newPostData, platform: e.target.value })}
            >
              {accounts.map((account) => (
                <MenuItem key={account._id} value={account.platform}>
                  {account.platform.charAt(0).toUpperCase() + account.platform.slice(1)} - {account.accountName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Content"
            multiline
            rows={4}
            value={newPostData.content}
            onChange={(e) => setNewPostData({ ...newPostData, content: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Hashtags (comma separated)"
            value={newPostData.hashtags}
            onChange={(e) => setNewPostData({ ...newPostData, hashtags: e.target.value })}
            placeholder="#RVLife, #Camping, #Outdoors"
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Post Type</InputLabel>
            <Select
              value={newPostData.type}
              label="Post Type"
              onChange={(e) => setNewPostData({ ...newPostData, type: e.target.value })}
            >
              <MenuItem value="promotional">Promotional</MenuItem>
              <MenuItem value="event">Event</MenuItem>
              <MenuItem value="amenity">Amenity</MenuItem>
              <MenuItem value="seasonal">Seasonal</MenuItem>
              <MenuItem value="engagement">Engagement</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            label="Schedule For"
            type="datetime-local"
            value={newPostData.scheduledFor}
            onChange={(e) => setNewPostData({ ...newPostData, scheduledFor: e.target.value })}
            InputLabelProps={{ shrink: true }}
            helperText="Leave empty to publish immediately"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatePostDialog(false)}>Cancel</Button>
          <Button onClick={handleCreatePost} variant="contained">
            {newPostData.scheduledFor ? 'Schedule Post' : 'Publish Now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Campaign Dialog */}
      <Dialog open={createCampaignDialog} onClose={() => setCreateCampaignDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Marketing Campaign</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Campaign Name"
            value={newCampaignData.name}
            onChange={(e) => setNewCampaignData({ ...newCampaignData, name: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Description"
            multiline
            rows={2}
            value={newCampaignData.description}
            onChange={(e) => setNewCampaignData({ ...newCampaignData, description: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Campaign Type</InputLabel>
            <Select
              value={newCampaignData.type}
              label="Campaign Type"
              onChange={(e) => setNewCampaignData({ ...newCampaignData, type: e.target.value })}
            >
              <MenuItem value="promotional">Promotional</MenuItem>
              <MenuItem value="seasonal">Seasonal</MenuItem>
              <MenuItem value="event">Event</MenuItem>
              <MenuItem value="engagement">Engagement</MenuItem>
              <MenuItem value="review_generation">Review Generation</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Platforms</InputLabel>
            <Select
              multiple
              value={newCampaignData.platforms}
              label="Platforms"
              onChange={(e) => setNewCampaignData({ 
                ...newCampaignData, 
                platforms: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value 
              })}
            >
              {accounts.map((account) => (
                <MenuItem key={account._id} value={account.platform}>
                  {account.platform.charAt(0).toUpperCase() + account.platform.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateCampaignDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateCampaign} variant="contained">Create Campaign</Button>
        </DialogActions>
      </Dialog>

      {/* Review Response Dialog */}
      <Dialog open={reviewResponseDialog} onClose={() => setReviewResponseDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Respond to Review</DialogTitle>
        <DialogContent>
          {selectedReview && (
            <Box mb={2}>
              <Typography variant="subtitle1" gutterBottom>
                Review from {selectedReview.reviewer.name}
              </Typography>
              <Rating value={selectedReview.rating} readOnly size="small" />
              <Typography variant="body2" paragraph>
                {selectedReview.content}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label="Your Response"
            multiline
            rows={4}
            value={reviewResponse}
            onChange={(e) => setReviewResponse(e.target.value)}
            placeholder="Thank you for your feedback..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewResponseDialog(false)}>Cancel</Button>
          <Button onClick={handleRespondToReview} variant="contained">Send Response</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SocialMediaPage;